import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

/**
 * Searchable ISO 4217 currency picker. Stores the 3-letter code (e.g. "USD")
 * — exactly what the API's CurrencyCode char(3) column expects — and labels
 * each option with its display name (e.g. "USD — US Dollar"), searchable by
 * both. Reusable across warehouse / tenant / item forms.
 *
 * The code list comes from Intl.supportedValuesOf('currency') on modern engines
 * (Chromium, which the PWA targets); older engines fall back to a curated list.
 */

const FALLBACK_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'MXN', 'JPY', 'CNY', 'AUD', 'CHF', 'INR',
];

function supportedCurrencies(): string[] {
  try {
    const withValues = Intl as typeof Intl & {
      supportedValuesOf?: (key: 'currency') => string[];
    };
    const list = withValues.supportedValuesOf?.('currency');
    if (list && list.length > 0) return list;
  } catch {
    // fall through to the curated list
  }
  return FALLBACK_CURRENCIES;
}

// Built once at module load. Names are in English (the code is the universal
// key and the primary way users search); a localized name is a nice-to-have.
const BASE_OPTIONS: { value: string; label: string }[] = (() => {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames(['en'], { type: 'currency' });
  } catch {
    names = undefined;
  }
  return supportedCurrencies().map((code) => {
    let name = code;
    try {
      name = names?.of(code) ?? code;
    } catch {
      name = code;
    }
    return { value: code, label: name && name !== code ? `${code} — ${name}` : code };
  });
})();

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

export function CurrencySelect({ value, onChange, ...rest }: Props) {
  const { t } = useTranslation();

  // Keep the current value selectable even if the runtime didn't list it.
  const data = useMemo(() => {
    const upper = value?.toUpperCase();
    if (upper && !BASE_OPTIONS.some((o) => o.value === upper)) {
      return [{ value: upper, label: upper }, ...BASE_OPTIONS];
    }
    return BASE_OPTIONS;
  }, [value]);

  return (
    <Select
      {...rest}
      data={data}
      value={value ? value.toUpperCase() : null}
      onChange={(v) => onChange(v ?? '')}
      searchable
      allowDeselect={false}
      nothingFoundMessage={t('shell.noMatch')}
      maxDropdownHeight={280}
      comboboxProps={{ shadow: 'md' }}
    />
  );
}
